<template>
  <div class="client-badge-with-activity-status">
    <component :is="clickable ? 'a' : 'Transition'" :href="clientUrl">
      <client-badge
        :client="client"
        :show-location="false"
        :show-room-number="showRoomNumber"
        :show-escalation-level="showEscalationLevel"
        :no-background-colour="noBackgroundColour"
        :date-context="dateContext"
        class="me-1 d-inline-block"
        data-testid="client-badge"
        @click="onClientBadgeClick"
      />
    </component>
    <span v-if="retryTimestamp" :class="retryTimestampClasses">
      Last tried {{ retryTimestamp }}
    </span>
    <span
      v-else-if="!client.startDate && !client.endDate"
      :class="notStartedClasses"
      data-testid="activity-status-text"
    >
      Not started
    </span>
    <span
      v-if="isStarted && currentlyUnavailable === false"
      :class="startedClasses"
      data-testid="activity-status-text"
    >
      <font-awesome-icon fixed-width icon="play" />
      Started
      {{ startedTimeText }}
      <template v-if="showStaff">
        by
        <user-display-resolver
          :user-id="getStartedReport.userId"
          :display-name="getStartedReport.displayName"
          :scale="0.5"
        />
      </template>
    </span>
    <span
      v-if="isStarted && currentlyUnavailable !== false"
      class="badge bg-warning text-dark"
    >
      <font-awesome-icon fixed-width icon="hourglass-start" />
      {{ currentlyUnavailable }} at
      {{ formatTimeHoursMins(currentlyUnavailableTime) }}
      <template v-if="showStaff">
        by
        <user-display-resolver
          :user-id="getStartedReport.userId"
          :display-name="getStartedReport.displayName"
          :scale="0.5"
        />
      </template>
    </span>
    <span
      v-if="client.endDate"
      class="badge bg-success"
      data-testid="activity-status-text"
    >
      <font-awesome-icon fixed-width icon="check" />
      Finished
      {{ formatTimeHoursMins(client.endDate) }}
      <font-awesome-icon icon="clock" />
      {{
        formatDuration(
          intervalToDuration({
            start: client.startDate,
            end: client.endDate,
          }),
          { format: ["months", "weeks", "days", "hours", "minutes"] },
        )
      }}
    </span>
  </div>
</template>

<script>
import find from "lodash/find";
import get from "lodash/get";
import has from "lodash/has";
import { Transition, computed } from "vue";
import { useRouter } from "vue-router";
import {
  differenceInDays,
  differenceInHours,
  formatDuration,
  intervalToDuration,
  formatDistanceToNowStrict,
  formatDistance,
} from "date-fns";
import ClientBadge from "./ClientBadge.vue";
import { formatTimeHoursMins } from "../../lib/date-functions";
import { reportEvents } from "../../lib/constants/activity-report-statuses";
import UserDisplayResolver from "@/components/UserDisplayResolver.vue";

export default {
  name: "ClientBadgeWithActivityStatus",
  components: {
    ClientBadge,
    Transition,
    UserDisplayResolver,
  },
  props: {
    client: {
      type: Object,
      required: true,
    },
    showRoomNumber: {
      type: [Boolean, String],
      default: () => "auto",
    },
    showStaff: {
      type: Boolean,
      default() {
        return true;
      },
    },
    showEscalationLevel: {
      type: Boolean,
      default() {
        return true;
      },
    },
    noBackgroundColour: {
      type: Boolean,
      default() {
        return false;
      },
    },
    clickable: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Object,
      default: () => ({}),
    },
    dateContext: {
      type: Date,
      default: () => new Date(),
    },
  },
  setup(props) {
    const router = useRouter();
    const opts = {
      noStyling: false,
      ...props.options,
    };

    const retryTimestamp = computed(() => {
      if (has(props.client, "retry")) {
        return formatDistance(props.client.retry.timestamp, new Date(), {
          addSuffix: true,
        });
      }
      return "";
    });

    const onClientBadgeClick = () => {
      if (!props.clickable) {
        return;
      }
      router.push({
        name: "client/ClientView",
        params: { clientId: props.client.clientId },
      });
    };

    return {
      onClientBadgeClick,
      formatTimeHoursMins,
      formatDuration,
      intervalToDuration,
      retryTimestamp,
      opts,
    };
  },
  computed: {
    isStarted() {
      return this.client.startDate && !this.client.endDate;
    },
    clientUrl() {
      if (!this.clickable) {
        return undefined;
      }
      return `/clients/${this.client.clientId}`;
    },
    /**
     * Returns falsey if they were available, or the exact event type if
     * they were not available to start this
     */
    currentlyUnavailable() {
      const latest = get(this.client, "reports", []).slice(-1);
      if (latest === undefined || latest.length !== 1) {
        return false;
      }
      switch (latest[0].event) {
        case reportEvents.REFUSED:
        case reportEvents.UNAVAILABLE:
          return latest[0].event;
        default:
          return false;
      }
    },
    currentlyUnavailableTime() {
      if (this.currentlyUnavailable === false) {
        return null;
      }
      const latest = get(this.client, "reports", []).slice(-1);
      if (latest === undefined || latest.length !== 1) {
        return null;
      }
      return latest[0].date;
    },
    getStartedReport() {
      return find(this.client.reports, { event: "STARTED" });
    },
    startedAgo() {
      const { startDate } = this.client;
      const now = new Date();
      const days = differenceInDays(now, startDate);
      const hours = differenceInHours(now, startDate);
      return { startDate, days, hours };
    },
    notStartedClasses() {
      if (this.opts.noStyling) {
        return [];
      }
      return ["badge", "bg-danger"];
    },
    startedClasses() {
      if (this.opts.noStyling) {
        return [];
      }
      const { days, hours } = this.startedAgo;
      if (days === 0 && hours < 12) {
        return ["badge", "bg-success"];
      } // else
      return ["badge", "bg-warning", "text-dark"];
    },
    retryTimestampClasses() {
      if (this.opts.noStyling) {
        return [];
      }
      return ["badge", "bg-secondary"];
    },
    startedTimeText() {
      const { startDate, days, hours } = this.startedAgo;
      if (days >= 1) {
        return formatDistanceToNowStrict(startDate, {
          unit: "day",
          addSuffix: true,
        });
      } // else
      if (hours >= 12) {
        return formatDistanceToNowStrict(startDate, {
          unit: "hour",
          addSuffix: true,
        });
      } // else
      return formatTimeHoursMins(startDate);
    },
  },
};
</script>

<style>
.client-badge-with-activity-status {
  :deep(.badge) {
    /* allow badges to overflow to multiple lines */
    white-space: normal !important;
  }

  a > span > span.client-badge:hover {
    text-decoration: underline;
  }
}
</style>
